import type { Accessor, JSX } from 'solid-js'
import { Show } from 'solid-js'
import { wrapText } from './fitText'
import { verticalAlignOffset, interpolateTemplate } from './BaseTextObject'
import { setTemplateAndResizeCmd, fieldReserveCmd } from '../../project/commands'
import {
	AbstractTextImpl,
	type ITextImpl,
	type SetPosFn
} from './AbstractTextImpl'
import type { HugFit, ResizeHandles, GeometrySeed } from '../interactions/ObjectImpl'
import { defaultSeedBox } from 'Shared/posters/canvas/interactions/ObjectImpl'
import type { RuntimeText, DataRecord, Box, Position, Dimensions } from '../../types'
import type { Style } from '../../project/posterSchema'
import type { ObjectContext } from '../../project/objects'
import type { ImplRegistry } from '../interactions/ObjectImpl'
import type { HistoryContext } from '../../project/history'
import { SetStoreFunction } from 'solid-js/store'

export type PendingResize = { newTop: number; newHeight: number }

export function wrappedHeight(text: string, width: number, effStyle: Accessor<Style | undefined>, lineHeight: Accessor<number>): number
{
	const font  = (effStyle()?.fontSize ?? 24) as number
	const lines = wrapText(text, width, { ...effStyle(), fontSize: font })
	return Math.max(20, Math.ceil(lines.length * font * lineHeight()))
}

export function reservedHeight(objectHeight: number | undefined, effStyle: Accessor<Style | undefined>, lineHeight: Accessor<number>, fieldWidth: Accessor<number>, resolved: Accessor<string>): number
{
	if (objectHeight != null) return objectHeight
	return wrappedHeight(resolved(), fieldWidth(), effStyle, lineHeight)
}

export class TemplateTextImpl extends AbstractTextImpl implements ITextImpl
{
	static initialSeed(object: RuntimeText, record: DataRecord, effStyle: Accessor<Style | undefined>, lineHeight: Accessor<number>, layoutPos?: () => Position | undefined): GeometrySeed
	{
		const width    = object.size?.width ?? 200
		const font     = (effStyle()?.fontSize ?? 24) as number
		const minLines = (object.content ?? '').split('\n').length
		const resolved = interpolateTemplate(object.content ?? '', record, false)
		const lines    = Math.min(wrapText(resolved, width, { ...effStyle(), fontSize: font }).length, minLines)
		const content  = Math.max(20, Math.ceil(lines * font * lineHeight()))
		const reserved = Math.ceil(minLines * font * lineHeight())
		const height   = object.size?.height ?? Math.max(content, reserved)
		return { box: { pos: object.position ?? defaultSeedBox.pos, dims: { width, height } }, layoutPos }
	}

	constructor(
		object:     RuntimeText,
		userFacing: Accessor<boolean>,
		canvas:     Dimensions,
		effStyle:   Accessor<Style | undefined>,
		lineHeight: Accessor<number>,
		textAlign:  Accessor<string>,
		textRef:    (() => SVGTextElement | null | undefined) | undefined,
		getContentRef: () => SVGGElement | undefined,
		private record:           DataRecord,
		private missingAsEmpty:   boolean,
		private widthOverride:    Accessor<number | null>,
		private setWidthOverride: (v: number | null) => void,
		private editState:        { hasBeenEdited: boolean; pendingResize: PendingResize | null },
		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		contentOffsetY:    Accessor<number>,
		setContentOffsetY: (v: number) => void,
		private cropToMinLines = false,
	) {
		super(object, userFacing, canvas, effStyle, lineHeight, textAlign, textRef, getContentRef, box,setBox,seed,contentOffsetY,setContentOffsetY);
	}

	boxWidth() { return this.fieldWidth() }
	fieldHeight() { return this.box.dims.height ?? this.getWrappedHeight(this.fieldWidth()) }

	private resolved(): string {
		return interpolateTemplate(this.object.content ?? '', this.record, this.missingAsEmpty)
	}

	fieldWidth(): number {
		return this.widthOverride() ?? this.object.size?.width ?? 200
	}

	private reservedHeight(): number {
		return reservedHeight(this.object.size?.height, this.effStyle, this.lineHeight, () => this.fieldWidth(), () => this.resolved())
	}

	getHugFit(): HugFit | undefined {
		const width   = this.fieldWidth()
		const content = this.getWrappedHeight(width)
		const reserved = this.cropToMinLines ? this.minHeight() : this.reservedHeight()
		const height  = Math.max(content, reserved)
		const top     = this.anchorTop(this.box.pos.y ?? 0, this.box.dims.height ?? height, height)
		const measuredDy = this.measureContentDy()
		const dy = (measuredDy ?? 0) - verticalAlignOffset(this.object.alignment, height, content)
		// A template field only anchors vertically; x is its unchanged design-left so pos stays whole.
		return { box: { pos: { x: this.box.pos.x ?? 0, y: top }, dims: { width, height } }, dy, measured: measuredDy !== null }
	}

	private measureContentDy(): number | null {
		const ref = this.getContentRef()
		if (!ref) return null
		try {
			const bbox = ref.getBBox()
			if (bbox.width === 0 && bbox.height === 0) return null
			return Math.floor(bbox.y)
		} catch {
			return null
		}
	}

	yOffset():     number { return 0 }
	overrideFontSize(): number | null { return this.designFont() }
	initialText(): string { return this.object.content ?? '' }
	getText():     string { return this.object.content ?? '' }
	onFieldWidthResize(w: number): void { this.setWidthOverride(w) }
	onFieldWidthResizeEnd(newLeft: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void { this.saveFieldWidthResize(newLeft, objCtx, impls, histCtx) }

	designFont(): number { return (this.effStyle()?.fontSize ?? 24) as number }

	getWrappedHeight(w: number): number {
		return Math.max(20, Math.ceil(this.wrappedLines(w).length * this.designFont() * this.lineHeight()))
	}

	private wrappedLines(width: number): string[] {
		const lines = wrapText(this.displayText(), width, { ...this.effStyle(), fontSize: this.designFont() })
		return this.cropToMinLines ? lines.slice(0, this.minLines()) : lines
	}

	private anchorTop(prevTop: number, prevHeight: number, newHeight: number): number {
		const alignment = this.object.alignment ?? 'top'
		if (alignment === 'center') return Math.round(prevTop + (prevHeight - newHeight) / 2)
		if (alignment === 'bottom') return Math.round(prevTop + prevHeight - newHeight)
		return prevTop
	}

	onCornerResize(_fontSize: number): void {}
	onCornerResizeEnd(_box: Box): void {}
	startFontSize(): number | undefined { return undefined }
	onWidthResize(_w: number): void {}
	onWidthResizeEnd(_box: Box): void {}

	resizeHandles() { return 'fieldBox' as ResizeHandles; }

	// A template field always draws the "open" selection box (side ticks + a single line on the aligned
	// edge), whose geometry comes from selectionBox(). Every other object keeps ObjectImpl's closed rect.
	override selectionComponent(): JSX.Element {
		return <TextSelection impl={this} />
	}

	fitAfterEdit(_snap: Box, setPos: SetPosFn): boolean {
		this.applyPendingResize(setPos)
		return true
	}

	onTextEdit(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void {
		this.editState.pendingResize = this.templateResize(this.editState, text, objCtx, impls, histCtx)
	}

	getWrappedLines(_width: Accessor<number>): (() => string[]) | undefined {
		return () => this.wrappedLines(this.fieldWidth())
	}

	public displayText(): string {
		return this.resolved()
	}

	private applyPendingResize(setPos: SetPosFn): void {
		if (this.editState.pendingResize === null) return
		setPos({ pos: { x: this.box.pos.x, y: this.editState.pendingResize.newTop }, dims: { width: this.box.dims.width, height: this.editState.pendingResize.newHeight } })
		this.editState.pendingResize = null
	}

	private saveFieldWidthResize(newLeft: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void {
		const { object } = this
		const w = this.fieldWidth()
		this.setWidthOverride(null)
		const font   = this.designFont()
		// The committed reserve must stay on the minLines-cropped basis the design display, seed and
		// refit all use — the full resolved record text can wrap further and would inflate it.
		const height = Math.max(this.getWrappedHeight(w), this.minHeight())
		const newTop = this.box.pos.y ?? object.position?.y ?? 0
		histCtx.execute(fieldReserveCmd(objCtx, impls, object.id,
			{
				fontSize: (object.style?.fontSize as number | undefined) ?? null,
				box: {
					pos:  { x: object.position?.x ?? 0, y: object.position?.y ?? 0 },
					dims: { width: object.size?.width, height: object.size?.height },
				},
			},
			{ fontSize: font, box: { pos: { x: newLeft, y: newTop }, dims: { width: w, height } } },
		))
	}

	templateResize(
		state: { hasBeenEdited: boolean },
		text: string,
		objCtx: ObjectContext,
		impls: ImplRegistry,
		histCtx: HistoryContext,
	): PendingResize | null
	{
		const { object, canvas, effStyle, lineHeight } = this
		const prevTemplate = object.content ?? ''
		const prevLines    = prevTemplate.split('\n').length
		const newLines     = text.split('\n').length
		const canAutoResize = object.size?.height == null
		const needsResize  = canAutoResize && (!state.hasBeenEdited || prevLines !== newLines)
		state.hasBeenEdited = true

		const prevTop      = object.position?.y ?? 0
		let newTop         = prevTop
		let pendingResize: PendingResize | null = null

		if (needsResize) {
			const fontSize  = (effStyle()?.fontSize ?? 24) as number
			const newHeight = Math.ceil(newLines * fontSize * lineHeight())
			const oldHeight = this.reservedHeight()
			const alignment = object.alignment ?? 'top'

			if (alignment === 'center') {
				newTop = Math.round(prevTop + (oldHeight - newHeight) / 2)
			} else if (alignment === 'bottom') {
				newTop = Math.round(prevTop + oldHeight - newHeight)
			}

			const posterH = canvas.height ?? 9999
			newTop = Math.max(0, Math.min(newTop, posterH - newHeight))
			pendingResize = { newTop, newHeight }
		}

		histCtx.execute(setTemplateAndResizeCmd(
			objCtx, impls, object.id,
			prevTemplate, text,
			prevTop, newTop,
		))
		return pendingResize
	}
}

// The "open" selection outline for a design-mode template field: side ticks sideH tall from sideTop,
// plus a single horizontal line on the bounded (aligned) edge. Geometry comes from selectionBox(h).
function TextSelection(P: { impl: ITextImpl })
{
	const selW = () => P.impl.getSize().width;
	const selH = () => P.impl.getSize().height;
	const selBox = () => P.impl.selectionBox(selH());

	return (<>
		<Show when={selBox().hLineTop}>
			<line x1="0" y1={selBox().sideTop} x2={selW()} y2={selBox().sideTop}
				stroke="#4d9eff" stroke-width="1.5" stroke-dasharray="4 3" />
		</Show>
		<Show when={selBox().hLineBottom}>
			<line x1="0" y1={selBox().sideTop + selBox().sideH} x2={selW()} y2={selBox().sideTop + selBox().sideH}
				stroke="#4d9eff" stroke-width="1.5" stroke-dasharray="4 3" />
		</Show>
		<line x1="0" y1={selBox().sideTop} x2="0" y2={selBox().sideTop + selBox().sideH}
			stroke="#4d9eff" stroke-width="1.5" stroke-dasharray="4 3" />
		<line x1={selW()} y1={selBox().sideTop} x2={selW()} y2={selBox().sideTop + selBox().sideH}
			stroke="#4d9eff" stroke-width="1.5" stroke-dasharray="4 3" />
	</>);
}

